/**
 * Shared text-cleanup helpers for export (PDF + Word).
 *
 * The proposal content occasionally carries HTML entities (e.g. `VA&apos;s`),
 * leftover markdown, or signature artefacts ("Respectfully submitted,," and
 * `[AUTHORIZED REPRESENTATIVE]` placeholders). Both export paths must produce
 * clean, submission-ready prose, so the cleanup lives in one place.
 */

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  apos: "'",
  quot: '"',
  lt: '<',
  gt: '>',
  nbsp: ' ',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  rsquo: '’',
  lsquo: '‘',
  rdquo: '”',
  ldquo: '“',
  copy: '©',
  reg: '®',
  trade: '™',
  deg: '°',
};

/** Decode HTML entities (named + numeric) into real characters. */
export function decodeHtmlEntities(input: string): string {
  if (!input || input.indexOf('&') === -1) return input;
  return input
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => safeFromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => safeFromCodePoint(parseInt(dec, 10)))
    .replace(/&([a-zA-Z][a-zA-Z0-9]*);/g, (m, name) =>
      Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, name) ? NAMED_ENTITIES[name] : m
    );
}

function safeFromCodePoint(code: number): string {
  try {
    return Number.isFinite(code) ? String.fromCodePoint(code) : '';
  } catch {
    return '';
  }
}

/**
 * Join a closing salutation with the signer name without doubling punctuation.
 * "Respectfully submitted," already ends in a comma, so we don't append another.
 */
export function formatClosing(salutation?: string | null): string {
  const s = (salutation || '').trim();
  if (!s) return '';
  return /[,.;:]$/.test(s) ? s : `${s},`;
}

/** A value is a leftover template placeholder like `[AUTHORIZED REPRESENTATIVE]`. */
export function isPlaceholder(value?: string | null): boolean {
  const s = (value || '').trim();
  if (!s) return true;
  return /^\[[^\]]*\]$/.test(s) || /^(tbd|to be completed|n\/?a)$/i.test(s);
}

/** First non-placeholder, non-empty string from the candidates, or '' . */
export function firstReal(...candidates: Array<string | null | undefined>): string {
  for (const c of candidates) {
    if (c && !isPlaceholder(c)) return c.trim();
  }
  return '';
}

/** Tidy whitespace and orphaned punctuation left behind after removing inline tags. */
function tidySpacing(s: string): string {
  return s
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([.,;:)])/g, '$1')
    .replace(/\(\s+/g, '(')
    .replace(/\s+\n/g, '\n')
    .trim();
}

/** Remove gap/placeholder markers (e.g. [DATA NEEDED], [TBD]) that must never ship. */
export function stripGapMarkers(s: string): string {
  if (!s) return s;
  return tidySpacing(
    s.replace(/\s*\[\s*(data needed|tbd|to be determined|to be completed|placeholder|insert[^\]]*)\s*\]/gi, ' ')
  );
}

/**
 * Submission-ready cleanup: remove ALL inline bracket annotations — source
 * citations ([SAC-1]), evidence/reference tags ([Key Personnel, PWS 12.2]),
 * and gap markers ([DATA NEEDED]). Federal proposal prose does not use square
 * brackets, so this is safe for the clean ("citations off") export.
 */
export function stripBracketAnnotations(s: string): string {
  if (!s) return s;
  return tidySpacing(s.replace(/\s*\[[^\]\n]{1,90}\]/g, ' '));
}

/** Trim a title and strip trailing/leading separator punctuation (e.g. "… Vets -"). */
export function cleanTitle(s: string): string {
  if (!s) return '';
  return decodeHtmlEntities(s)
    .replace(/\s+/g, ' ')
    .replace(/^[\s\-–—|:·]+/, '')
    .replace(/[\s\-–—|:·]+$/, '')
    .trim();
}
